import {
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react'

import { useAudioPosition } from 'react-use-audio-player'

import { useIsMobile } from 'hooks/useIsMobile'
import { fastdom } from 'utils/fastdom'

import { analyticsPlayers, track } from './analytics'
import {
  StyledAudioPlayerProgress,
  StyledAudioPlayerProgressBar,
  StyledAudioTrackProgress,
  StyledAudioTrackTime,
} from './index.styled'

const pad = (n: number) => (n < 10 ? `0${n}` : n.toString())

export const formatTime = (n: number) => {
  const minutes = Math.floor(n / 60)
  const seconds = Math.round(n % 60)
  return `${pad(minutes)}:${pad(seconds)}`
}

type ProgressProps = {
  percent: number
  onClick?: MouseEventHandler<HTMLDivElement>
}

export const Progress = forwardRef(function Progress(
  { percent, onClick }: ProgressProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <StyledAudioPlayerProgress ref={ref} onClick={onClick}>
      <StyledAudioPlayerProgressBar style={{ width: `${percent}%` }} />
    </StyledAudioPlayerProgress>
  )
})

type AudioTrackProgressProps = {
  articleId: number
}

export function AudioTrackProgress({ articleId }: AudioTrackProgressProps) {
  const { percentComplete, position, seek, duration } = useAudioPosition()
  const ref = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const totalTime = useMemo(() => formatTime(duration), [duration])

  const timeSeconds = useMemo(
    () => Math.round((percentComplete * duration) / 100),
    [percentComplete, duration],
  )

  const goTo = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      const { pageX: eventOffsetX } = event
      fastdom.measure(() => {
        if (ref.current) {
          const {
            x: elementOffsetX,
            width: elementWidth,
          } = ref.current.getBoundingClientRect()
          const percent = (eventOffsetX - elementOffsetX) / elementWidth
          const time = percent * duration
          seek(time)
          const player = analyticsPlayers[articleId]
          if (player) {
            player.currentTime = Math.round(time)
            track(articleId, 'forward')
          }
        }
      })
    },
    [duration, seek, articleId],
  )

  useEffect(() => {
    const player = analyticsPlayers[articleId]
    if (player) {
      player.currentTime = timeSeconds
    }
  }, [timeSeconds, articleId])

  return (
    <StyledAudioTrackProgress>
      <StyledAudioTrackTime
        size={isMobile ? 'xs' : 'p3'}
        className="mobile-navigation-label"
      >
        {formatTime(position)} / {totalTime}
      </StyledAudioTrackTime>
      {!isMobile && (
        <Progress onClick={goTo} ref={ref} percent={percentComplete} />
      )}
    </StyledAudioTrackProgress>
  )
}
